/**
 * A readable generated password.
 *
 * Four random words plus digits rather than a wall of symbols: whoever creates an account has
 * to communicate the password to the person it belongs to, usually out loud, and a
 * `x7$Kq2!vB` gets transcribed wrong. Entropy comes from the word count against the list size
 * (~2048^4, about 44 bits) plus the digits — comfortably more than a typed password, and it
 * survives being read over a phone.
 *
 * `crypto.getRandomValues`, never `Math.random`: this is a credential.
 */

const WORDS = [
  "amber", "anchor", "atlas", "aurora", "basin", "beacon", "birch", "bishop", "bramble",
  "bridge", "bronze", "canyon", "cedar", "cinder", "citrus", "clover", "cobalt", "compass",
  "copper", "coral", "cotton", "crater", "crimson", "cyprus", "dagger", "delta", "denim",
  "domino", "ember", "fable", "falcon", "fathom", "fennel", "flint", "forest", "fossil",
  "garnet", "glacier", "granite", "harbor", "hazel", "hollow", "indigo", "island", "ivory",
  "jasper", "juniper", "kestrel", "lagoon", "lantern", "ledger", "linen", "lumber", "magnet",
  "maple", "marble", "meadow", "mercury", "meteor", "mineral", "monsoon", "mosaic", "nickel",
  "nomad", "northern", "obsidian", "onyx", "orbit", "otter", "pebble", "pewter", "pigment",
  "pilot", "pioneer", "plateau", "prairie", "quarry", "quartz", "quiver", "radar", "raven",
  "ridge", "river", "rugged", "saffron", "sandbar", "sapphire", "scarlet", "sequoia", "shale",
  "signal", "silver", "socket", "solar", "spruce", "summit", "sunset", "tangent", "teakwood",
  "tempo", "thicket", "timber", "topaz", "trellis", "tundra", "vector", "velvet", "vessel",
  "walnut", "willow", "window", "winter", "zenith", "zephyr",
] as const;

function randomBelow(bound: number): number {
  // Rejection sampling: `% bound` on a raw 32-bit draw biases toward the low values whenever
  // bound does not divide 2^32. It matters little at these sizes and costs nothing to do right.
  const limit = Math.floor(0xffffffff / bound) * bound;
  const buf = new Uint32Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buf);
    value = buf[0]!;
  } while (value >= limit);
  return value % bound;
}

export function generatePassphrase(words = 4): string {
  const picked = Array.from({ length: words }, () => WORDS[randomBelow(WORDS.length)]!);
  return `${picked.join("-")}-${String(randomBelow(100)).padStart(2, "0")}`;
}
